// client/src/utils/cacheBootstrap.ts
// Cache hydration utility for synchronous cache reading with race condition prevention

interface NormalizedCacheData {
  summary: any | null;
  blueprint: any | null;
  chapters: any[] | null;
  hydrated: boolean;
  timestamps: {
    summary?: string;
    blueprint?: string;
    chapters?: string;
  };
  metadata: {
    chaptersCount: number;
    hasValidTimestamps: boolean;
    cacheQuality: 'high' | 'medium' | 'low';
  };
}

/**
 * Tolerant timestamp picker that handles multiple cache formats
 */
function pickTimestamp(src: any): string | null {
  if (!src) return null;

  // Try multiple timestamp locations
  return src.updatedAt ||
         src.timestamp ||
         src.data?.updatedAt ||
         src.data?.timestamp ||
         src.chaptersLastUpdate ||
         src.data?.chaptersLastUpdate ||
         src.lastModified ||
         src.data?.lastModified ||
         src.blueprintData?.updatedAt ||
         null;
}

/**
 * Tolerant chapters array picker that handles multiple cache formats
 */
function pickChaptersArray(src: any): any[] | null {
  if (!src) return null;

  // Try multiple chapter locations
  return src.chapters ||
         src.data?.chapters ||
         (Array.isArray(src) ? src : null) ||
         src.items ||
         src.data?.items ||
         null;
}

/**
 * Extract chapters count from various sources
 */
function pickChaptersCount(src: any): number {
  const chaptersArray = pickChaptersArray(src);
  if (chaptersArray && Array.isArray(chaptersArray)) {
    return chaptersArray.length;
  }

  return src?.chaptersCount ||
         src?.data?.chaptersCount ||
         src?.chapterCount ||
         src?.data?.chapterCount ||
         0;
}

/**
 * Assess cache quality based on timestamp presence and data completeness
 */
function assessCacheQuality(
  summary: any,
  blueprint: any,
  chapters: any[],
  timestamps: { summary?: string; blueprint?: string; chapters?: string }
): 'high' | 'medium' | 'low' {
  const hasValidSummaryTs = !!timestamps.summary;
  const hasValidBlueprintTs = !!timestamps.blueprint;
  const hasValidChaptersTs = !!timestamps.chapters;
  const hasChapterData = chapters && chapters.length > 0;

  // High quality: has chapter data with timestamps
  if (hasChapterData && (hasValidChaptersTs || hasValidSummaryTs)) {
    return 'high';
  }

  // Medium quality: has metadata with timestamps but missing chapter data
  if ((hasValidSummaryTs || hasValidBlueprintTs) && pickChaptersCount(summary) > 0) {
    return 'medium';
  }

  // Low quality: missing essential timestamps or data
  return 'low';
}

/**
 * Synchronously read and normalize project caches to prevent race conditions
 * This should be called before any chapter state derivation
 */
export function readProjectCachesSync(projectUuid: string): NormalizedCacheData {
  try {
    // Read from localStorage synchronously
    const summaryKey = `storylikho_cache_project_summaries_${projectUuid}`;
    const blueprintKey = `storylikho_cache_blueprints_${projectUuid}`;
    const chaptersKey = `storylikho_cache_chapters_${projectUuid}`;

    // Also try alternative keys that might exist
    const altChaptersKey = `chapters_${projectUuid}`;

    // Parse cache entries
    let summary = null;
    let blueprint = null;
    let chapters = null;

    try {
      const summaryRaw = localStorage.getItem(summaryKey);
      if (summaryRaw) {
        const parsed = JSON.parse(summaryRaw);
        summary = parsed.data || parsed; // Handle both wrapped and unwrapped formats
      }
    } catch (e) {
      console.warn('Failed to parse summary cache:', e);
    }

    try {
      const blueprintRaw = localStorage.getItem(blueprintKey);
      if (blueprintRaw) {
        const parsed = JSON.parse(blueprintRaw);
        blueprint = parsed.data || parsed; // Handle both wrapped and unwrapped formats
      }
    } catch (e) {
      console.warn('Failed to parse blueprint cache:', e);
    }

    try {
      // Try primary chapters key first
      let chaptersRaw = localStorage.getItem(chaptersKey);
      if (!chaptersRaw) {
        // Try alternative key
        chaptersRaw = localStorage.getItem(altChaptersKey);
      }

      if (chaptersRaw) {
        const parsed = JSON.parse(chaptersRaw);
        chapters = pickChaptersArray(parsed) || parsed; // Handle both wrapped and unwrapped formats
      }
    } catch (e) {
      console.warn('Failed to parse chapters cache:', e);
    }

    // Extract timestamps using tolerant picker
    const timestamps = {
      summary: pickTimestamp(summary),
      blueprint: pickTimestamp(blueprint),
      chapters: pickTimestamp(chapters) || (chapters && chapters[0] ? pickTimestamp(chapters[0]) : null)
    };

    // Compute metadata
    const chaptersCount = Math.max(
      pickChaptersCount(summary),
      pickChaptersCount(blueprint),
      chapters ? chapters.length : 0
    );

    const normalizedTimestamps = {
      summary: timestamps.summary ?? undefined,
      blueprint: timestamps.blueprint ?? undefined,
      chapters: timestamps.chapters ?? undefined,
    };
    const hasValidTimestamps = !!(timestamps.summary || timestamps.blueprint || timestamps.chapters);
    const cacheQuality = assessCacheQuality(summary, blueprint, chapters, normalizedTimestamps);

    const result: NormalizedCacheData = {
      summary,
      blueprint,
      chapters,
      hydrated: true,
      timestamps: normalizedTimestamps,
      metadata: {
        chaptersCount,
        hasValidTimestamps,
        cacheQuality
      }
    };

    return result;

  } catch (error) {
    console.warn('Cache hydration failed:', error);
    return {
      summary: null,
      blueprint: null,
      chapters: null,
      hydrated: false,
      timestamps: {},
      metadata: {
        chaptersCount: 0,
        hasValidTimestamps: false,
        cacheQuality: 'low'
      }
    };
  }
}

/**
 * Check if caches are ready for chapter state derivation
 * This prevents the race condition where derivation runs before hydration
 */
export function areCachesReadyForDerivation(cacheData: NormalizedCacheData): boolean {
  if (!cacheData.hydrated) {
    return false;
  }

  // After migration-in-place, any hydrated cache is ready
  return true;
}

/**
 * Export timestamp and chapter picking utilities for use elsewhere
 */
export { pickTimestamp, pickChaptersArray, pickChaptersCount };